import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconPencil, IconPlus, IconStar, IconTrash } from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { DeleteFlowTemplateMutation, FlowTemplatesSubscription } from './-components/graphql';
import { readJsonArray } from './-components/json-array';

export const Route = createFileRoute('/_authed/system/flow-templates/')({
  component: SystemFlowTemplatesPage,
});

type FlowTemplateRow = {
  id: string;
  name: string;
  category: string | null;
  featured: boolean;
  sortOrder: number;
  variables: unknown;
  nodes: unknown;
};

function TemplateRow({ template, onDelete }: { template: FlowTemplateRow; onDelete: (id: string) => void }) {
  const variableCount = readJsonArray(template.variables).length;

  return (
    <TableTr>
      <TableTd>
        <Group gap="xs">
          {template.name}
          {template.featured && (
            <Badge size="xs" color="yellow" leftSection={<IconStar size={10} />}>
              Featured
            </Badge>
          )}
        </Group>
      </TableTd>
      <TableTd>
        <Text size="sm" c="dimmed">
          {template.category || '—'}
        </Text>
      </TableTd>
      <TableTd>
        <Badge size="sm" variant="light">
          {variableCount} var{variableCount !== 1 ? 's' : ''}
        </Badge>
      </TableTd>
      <TableTd>
        <Text size="sm" c="dimmed">
          {readJsonArray(template.nodes).length} nodes
        </Text>
      </TableTd>
      <TableTd>
        <Text size="xs" c="dimmed">
          Order: {template.sortOrder}
        </Text>
      </TableTd>
      <TableTd>
        <Group gap="xs">
          <Tooltip label="Edit">
            <ActionIcon
              variant="subtle"
              renderRoot={(props) => <Link to="/system/flow-templates/$id" params={{ id: template.id }} {...props} />}
            >
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete">
            <ActionIcon variant="subtle" color="red" onClick={() => onDelete(template.id)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </TableTd>
    </TableTr>
  );
}

function SystemFlowTemplatesPage() {
  const [{ data, error, fetching }] = useSubscription({ query: FlowTemplatesSubscription });
  const [, runDelete] = useMutation(DeleteFlowTemplateMutation);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const closeDeleteModal = () => {
    setDeletingId(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    setIsSubmitting(true);
    setDeleteError(null);

    const result = await runDelete({ id: deletingId });

    setIsSubmitting(false);

    if (result.error) setDeleteError(getErrorMessage(result.error, 'Failed to delete flow template'));
    else closeDeleteModal();
  };

  if (fetching && !data && !error) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const templates = data?.flowTemplates ?? [];

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>Flow Templates</Title>
              <Text c="dimmed" mt="xs">
                Create reusable flow templates with configurable variables
              </Text>
            </div>
            <Button component={Link} to="/system/flow-templates/new" leftSection={<IconPlus size={16} />}>
              New Template
            </Button>
          </Group>

          <ErrorAlert message={error ? getErrorMessage(error, 'Failed to load flow templates') : null} />

          <Paper withBorder radius="lg">
            <Table>
              <TableThead>
                <TableTr>
                  <TableTh>Name</TableTh>
                  <TableTh>Category</TableTh>
                  <TableTh>Variables</TableTh>
                  <TableTh>Nodes</TableTh>
                  <TableTh>Status</TableTh>
                  <TableTh w={120}>Actions</TableTh>
                </TableTr>
              </TableThead>
              <TableTbody>
                {templates.map((t) => (
                  <TemplateRow key={t.id} template={t} onDelete={setDeletingId} />
                ))}
                {templates.length === 0 && (
                  <TableTr>
                    <TableTd colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        No flow templates yet. Create your first template to get started.
                      </Text>
                    </TableTd>
                  </TableTr>
                )}
              </TableTbody>
            </Table>
          </Paper>
        </Stack>
      </Container>

      <Modal opened={!!deletingId} onClose={closeDeleteModal} title="Delete Flow Template">
        <Stack>
          <Text>Are you sure you want to delete this template? This action cannot be undone.</Text>
          <ErrorAlert message={deleteError} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void confirmDelete()} loading={isSubmitting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
